// routing
import Routes from 'routes';

// project imports
import Locales from 'ui-component/Locales';
import NavigationScroll from 'layout/NavigationScroll';
import RTLLayout from 'ui-component/RTLLayout';
import Snackbar from 'ui-component/extended/Snackbar';
import ThemeCustomization from 'themes';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from 'hooks/useAuth';

// auth provider
import { JWTProvider as AuthProvider } from 'contexts/JWTContext';

const AuthObserver = ({ children }: { children: React.ReactNode }) => {
    const { isLoggedIn, isInitialized, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!isInitialized) return;

        const excludedPaths = ['/setup-2fa', '/verify-2fa'];
        const publicPaths = ['/login', '/contact-us', '/faqs', '/', '/forgot-password', '/cert', '/reset-password', '/privacy-policy'];
        const isAdmin = Number(user?.role_id) === 1;

        if (excludedPaths.includes(location.pathname)) return;

        if (!isLoggedIn) {
            const requires2FA = user?.requires2FA;
            const setupRequired = user?.setupRequired;
            const hasTempToken = !!user?.tempToken;

            if (!isAdmin) {
                // 🔥 Send to setup-2fa if user has no secret or hasn't enabled 2FA
                if (requires2FA && hasTempToken && setupRequired) {
                    if (location.pathname !== '/setup-2fa') {
                        navigate('/setup-2fa');
                    }
                    return;
                }

                // 🔒 Send to verify-2fa if 2FA is enabled and just needs verification
                if (requires2FA && hasTempToken && !setupRequired) {
                    if (location.pathname !== '/verify-2fa') {
                        navigate('/verify-2fa');
                    }
                    return;
                }
            }
            // 🌐 For all other protected pages → redirect to login
            if (!publicPaths.includes(location.pathname)) {
                navigate('/login');
            }
        }
    }, [isInitialized, isLoggedIn, user, location.pathname]);

    return <>{children}</>;
};

const App = () => (
    <ThemeCustomization>
        <RTLLayout>
            <Locales>
                <NavigationScroll>
                    <AuthProvider>
                        <AuthObserver>
                            <Routes />
                            <Snackbar />
                        </AuthObserver>
                    </AuthProvider>
                </NavigationScroll>
            </Locales>
        </RTLLayout>
    </ThemeCustomization>
);

export default App;
